import isEmpty from 'lodash/isEmpty';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import entity from './entity';
import { setNotification } from '../../notification/actions';
import { getCurrentEmployer } from '../../features/employers/selectors';
import { getProfileKey } from '../../authentication/selectors';

export const TYPES = {
  ...entity.getTypes(),
  CREATE_CAMPAIGN_REQUEST: `${entity.name}/CREATE_CAMPAIGN_REQUEST`,
  CREATE_CAMPAIGN_FAILURE: `${entity.name}/CREATE_CAMPAIGN_FAILURE`,
  CREATE_CAMPAIGN_SUCCESS: `${entity.name}/CREATE_CAMPAIGN_SUCCESS`,
  GET_CAMPAIGNS_REQUEST: `${entity.name}/GET_CAMPAIGNS_REQUEST`,
  GET_CAMPAIGNS_FAILURE: `${entity.name}/GET_CAMPAIGNS_FAILURE`,
  GET_CAMPAIGNS_SUCCESS: `${entity.name}/GET_CAMPAIGNS_SUCCESS`,
  CREATE_SPONSORSHIP_REQUEST: `${entity.name}/CREATE_SPONSORSHIP_REQUEST`,
  CREATE_SPONSORSHIP_FAILURE: `${entity.name}/CREATE_SPONSORSHIP_FAILURE`,
  CREATE_SPONSORSHIP_SUCCESS: `${entity.name}/CREATE_SPONSORSHIP_SUCCESS`,
  UPDATE_JOB_CAMPAIGN_REQUEST: `${entity.name}/UPDATE_JOB_CAMPAIGN_REQUEST`,
  UPDATE_JOB_CAMPAIGN_SUCCESS: `${entity.name}/UPDATE_JOB_CAMPAIGN_SUCCESS`,
  UPDATE_JOB_CAMPAIGN_ERROR: `${entity.name}/UPDATE_JOB_CAMPAIGN_ERROR`,
  GET_SPONSORSHIPS_REQUEST: `${entity.name}/GET_SPONSORSHIPS_REQUEST`,
  GET_SPONSORSHIPS_FAILURE: `${entity.name}/GET_SPONSORSHIPS_FAILURE`,
  GET_SPONSORSHIPS_SUCCESS: `${entity.name}/GET_SPONSORSHIPS_SUCCESS`,
  GET_SPONSORSHIPS_HISTORY_REQUEST: `${entity.name}/GET_SPONSORSHIPS_HISTORY_REQUEST`,
  GET_SPONSORSHIPS_HISTORY_FAILURE: `${entity.name}/GET_SPONSORSHIPS_HISTORY_FAILURE`,
  GET_SPONSORSHIPS_HISTORY_SUCCESS: `${entity.name}/GET_SPONSORSHIPS_HISTORY_SUCCESS`,
  GET_CAMPAIGN_REQUEST: `${entity.name}/GET_CAMPAIGN_REQUEST`,
  GET_CAMPAIGN_FAILURE: `${entity.name}/GET_CAMPAIGN_FAILURE`,
  GET_CAMPAIGN_ERROR: `${entity.name}/GET_CAMPAIGN_ERROR`,
  GET_CAMPAIGN_SUCCESS: `${entity.name}/GET_CAMPAIGN_SUCCESS`,
  UPDATE_SPONSORSHIP_REQUEST: `${entity.name}/UPDATE_SPONSORSHIP_REQUEST`,
  UPDATE_SPONSORSHIP_FAILURE: `${entity.name}/UPDATE_SPONSORSHIP_FAILURE`,
  UPDATE_SPONSORSHIP_SUCCESS: `${entity.name}/UPDATE_SPONSORSHIP_SUCCESS`,
  CLEAR_SPONSORSHIP_ERROR: `${entity.name}/CLEAR_SPONSORSHIP_ERROR`,
  CLEAR_CAMPAIGN_ERROR: `${entity.name}/CLEAR_CAMPAIGN_ERROR`,
  CLEAR_STATE: `${entity.name}/CLEAR_STATE`,
};

export const createCampaign = (data, ctx) => async (dispatch, getState) => {
  dispatch({ type: TYPES.CREATE_CAMPAIGN_REQUEST });
  const state = getState();
  const profileKey = getProfileKey(state);
  const organizationId = getCurrentEmployer(state);
  try {
    const response = await entity.api.createCampaign({ query: { organization_id: organizationId }, data, profileKey, ctx });
    if (response?.data?.error) {
      // eslint-disable-next-line no-throw-literal
      throw {
        message: response?.data?.error,
        error_code: 400,
        errors: [],
      };
    }
    dispatch({ type: TYPES.CREATE_CAMPAIGN_SUCCESS, payload: { data: response?.data } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_CAMPAIGN_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating a campaign. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (isEmpty(error.errors) && (error.error_code === 400)) {
      dispatch(setNotification(`An error occurred while creating a campaign. ${error.message}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while creating a campaign.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const updateCampaignStatus = (campaignId, body) => async (dispatch, getState) => {
  dispatch({ type: TYPES.UPDATE_JOB_CAMPAIGN_REQUEST, payload: { data: { campaign_id: campaignId } } });
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const profileKey = getProfileKey(state);
    await entity.api.updateJobCampaign(campaignId, profileKey, { organization_id: organizationId }, body);
    const getResponse = await entity.api.getCampaign({ campaignId, organizationId });
    dispatch({
      type: TYPES.UPDATE_JOB_CAMPAIGN_SUCCESS,
      payload: {
        data: {
          campaign_id: campaignId,
          status: getResponse.data.status,
        },
        shouldUpdateRedux: true,
      },
    });
    return getResponse;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_JOB_CAMPAIGN_ERROR, payload: { error, data: { campaign_id: campaignId } } });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the campaign status. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while updating the campaign status.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const createSponsorship = body => async (dispatch, getState) => {
  dispatch({ type: TYPES.CREATE_SPONSORSHIP_REQUEST });
  try {
    const state = getState();
    const profileKey = getProfileKey(state);
    const organizationId = getCurrentEmployer(state);
    const response = await entity.api.createSponsorship({ organization_id: organizationId }, profileKey, body);
    dispatch({ type: TYPES.CREATE_SPONSORSHIP_SUCCESS });
    const responseData = response?.data;
    if (responseData?.status === 'success') {
      dispatch({ type: TYPES.CREATE_SPONSORSHIP_SUCCESS });
    } else {
      const error = { ...responseData, message: responseData?.errorMessage };
      dispatch({ type: TYPES.CREATE_SPONSORSHIP_FAILURE, payload: error });
      throw error;
    }
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_SPONSORSHIP_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating the sponsorship. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.errorMessage) {
      dispatch(setNotification('An error occurred while creating the sponsorship.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllCampaigns = options => async (dispatch, getState) => {
  dispatch({ type: TYPES.GET_CAMPAIGNS_REQUEST, payload: { page: options.page_current } });
  const profileKey = getProfileKey(getState());

  try {
    const response = await entity.api.getCampaigns(options, profileKey);
    const { data: { content } } = response;
    const pagination = {
      pageSize: response?.data.size,
      pageNumber: options.page_current,
      hasMore: response?.data.total_pages > options.page_current,
      totalElements: response?.data.totalElements,
    };
    const data = content.reduce((result, entity) => ({ ...result, [entity.campaign_id]: entity }), {});
    dispatch({ type: TYPES.GET_CAMPAIGNS_SUCCESS, payload: { content: data, pagination } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_CAMPAIGNS_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading campaigns. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (error?.error_code !== 403) {
      dispatch(setNotification('An error occurred while loading campaigns.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const updateJobCampaign = (campaignId, body, shouldUpdateRedux = true) => async (dispatch, getState) => {
  dispatch({ type: TYPES.UPDATE_JOB_CAMPAIGN_REQUEST });
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const profileKey = getProfileKey(state);
    const response = await entity.api.updateJobCampaign(campaignId, profileKey, { organization_id: organizationId }, body);
    delete response.sponsorship;
    dispatch({ type: TYPES.UPDATE_JOB_CAMPAIGN_SUCCESS, payload: { shouldUpdateRedux, data: response } });
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_JOB_CAMPAIGN_ERROR, payload: error });
    if (error.message) {
      dispatch(setNotification(`An error occurred while updating the campaign, ${error.message}.`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while updating the campaign.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllSponsorships = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_SPONSORSHIPS_REQUEST });
  try {
    const response = await entity.api.getSponsorships(options);
    const { data } = response;
    dispatch({ type: TYPES.GET_SPONSORSHIPS_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_SPONSORSHIPS_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading sponsorships. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading sponsorships.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllSponsorshipsHistory = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_SPONSORSHIPS_HISTORY_REQUEST });
  try {
    const response = await entity.api.getSponsorshipsHistory(options);
    const { data } = response;
    dispatch({ type: TYPES.GET_SPONSORSHIPS_HISTORY_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_SPONSORSHIPS_HISTORY_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading sponsorships history. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading sponsorships history.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getCampaignById = data => async (dispatch) => {
  const updatedData = { ...data, ...data?.sponsorship };
  updatedData.sponsorship_start_date = data?.sponsorship?.start_date;
  updatedData.sponsorship_end_date = data?.sponsorship?.end_date;
  dispatch({ type: TYPES.GET_REQUEST_SUCCESS, payload: { [updatedData?.campaign_id]: updatedData } });
};

export const getCampaign = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_CAMPAIGN_REQUEST });
  try {
    const response = await entity.api.getCampaign(options);
    const { data } = response;
    dispatch({ type: TYPES.GET_CAMPAIGN_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_CAMPAIGN_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading campaign. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading campaign', VARIANTS.INFO));
    }
    throw error;
  }
};

export const updateCampaignSponsorship = data => async (dispatch, getState) => {
  dispatch({ type: TYPES.UPDATE_SPONSORSHIP_REQUEST });
  try {
    const state = getState();
    const profileKey = getProfileKey(state);
    const currentEmployer = getCurrentEmployer(state);
    const response = await entity.api.updateCampaignSponsorship(data, { organization_id: currentEmployer }, profileKey);
    const responseData = response?.data;
    if (responseData?.status === 'success') {
      const sponsorship = responseData?.item;
      dispatch({ type: TYPES.UPDATE_SPONSORSHIP_SUCCESS, payload: { [sponsorship.id]: sponsorship } });
    } else {
      const error = { ...responseData, message: responseData?.errorMessage };
      dispatch({ type: TYPES.UPDATE_SPONSORSHIP_FAILURE, payload: error });
      throw error;
    }
    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_SPONSORSHIP_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the job campaign sponsorship. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.errorMessage) {
      dispatch(setNotification('An error occurred while updating the job campaign sponsorship.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const clearSponsorshipError = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_SPONSORSHIP_ERROR });
};

export const clearCampaignError = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_CAMPAIGN_ERROR });
};

export const clearState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_STATE });
};

export default {
  TYPES,
  ...entity.getActions(),
  createCampaign,
  getAllCampaigns,
  getCampaign,
  getAllSponsorships,
  getAllSponsorshipsHistory,
  updateJobCampaign,
  updateCampaignSponsorship,
  clearState,
  updateCampaignStatus,
  createSponsorship,
  clearSponsorshipError,
  clearCampaignError,
};
